import {baseApi} from 'core/redux/store/api';
import {ICart} from '../models';
import {IAddToCartParams, IGetCartParams, IUpdateCartParams} from '../types';
import {API_VERSION} from 'core/helpers/env';

const CART_MINI_QUERY_ARG: IGetCartParams = {mini: true};

const syncCartCacheOnMutation = async (
    _: unknown,
    {dispatch, queryFulfilled}: {dispatch: (action: unknown) => void; queryFulfilled: Promise<{data: ICart}>},
) => {
    try {
        const {data} = await queryFulfilled;

        if (!data) {
            return;
        }

        dispatch(cartService.util.updateQueryData('getCart', CART_MINI_QUERY_ARG, () => data));
    } catch {}
};

const clearCartCacheOnMutation = async (
    _: void,
    {dispatch, queryFulfilled}: {dispatch: (action: unknown) => void; queryFulfilled: Promise<unknown>},
) => {
    try {
        await queryFulfilled;
        dispatch(
            cartService.util.updateQueryData('getCart', CART_MINI_QUERY_ARG, (draft) => {
                if (!draft) {
                    return;
                }

                draft.products = [];
                draft.total.price = 0;
                draft.total.discount = 0;
            }),
        );
    } catch {}
};

export const cartService = baseApi.injectEndpoints({
    overrideExisting: true,
    // eslint-disable-next-line sort-keys
    endpoints: (build) => ({
        addToCart: build.mutation<ICart, IAddToCartParams>({
            onQueryStarted: syncCartCacheOnMutation,
            query: ({quantity, offerId}) => ({
                body: {
                    quantity,
                },
                method: 'POST',
                url: `${API_VERSION}/cart/product/${offerId}`,
            }),
            transformResponse: (response: {data: ICart}) => response?.data,
        }),
        clearCart: build.mutation<void, void>({
            onQueryStarted: clearCartCacheOnMutation,
            query: () => ({
                method: 'DELETE',
                url: `${API_VERSION}/cart`,
            }),
        }),
        deleteFromCart: build.mutation<ICart, Pick<IUpdateCartParams, 'id'>>({
            onQueryStarted: syncCartCacheOnMutation,
            query: ({id}) => ({
                method: 'DELETE',
                url: `${API_VERSION}/cart/product/${id}`,
            }),
            transformResponse: (response: {data: ICart}) => response?.data,
        }),
        getCart: build.query<ICart, IGetCartParams | void>({
            providesTags: ['Cart'],
            query: (params) => ({
                url: `${API_VERSION}/cart/product${Boolean(params?.mini) ? '?type=mini' : ''}`,
            }),
            transformResponse: (response: {data: ICart}) => response?.data,
        }),
        updateCart: build.mutation<ICart, IUpdateCartParams>({
            onQueryStarted: syncCartCacheOnMutation,
            query: ({quantity, id}) => ({
                body: {
                    quantity,
                },
                method: 'PATCH',
                url: `${API_VERSION}/cart/product/${id}`,
            }),
            transformResponse: (response: {data: ICart}) => response?.data,
        }),
    }),
});

export const {
    useGetCartQuery,
    useAddToCartMutation,
    useDeleteFromCartMutation,
    useClearCartMutation,
    useUpdateCartMutation,
} = cartService;

export const {getCart, addToCart, updateCart} = cartService.endpoints;
